import { useQuery } from 'react-query';
import * as React from 'react';
import { gamesUrls } from 'utils/apiUrls';
import { fetchGamesTracking } from 'pages/Dashboard/services/tracking.services';
import { StroopTimeSeriesAPIResponse } from 'pages/Dashboard/types/waypoints.types';
import { TimeFrameOptions } from 'pages/Dashboard/utils/trackingUtils';
import { useHttp } from 'hooks/use-fetch';

interface UseStroopDataProps {
  providerId: string | undefined;
  patientId: string | undefined;
  timeFrame: string;
}
function useStroopData({ providerId, patientId, timeFrame }: UseStroopDataProps) {
  // Query for current timeframe
  const { http } = useHttp();
  const stroopQuery = useQuery(
    [
      `${gamesUrls.stroopTimeSeries.queryUrl}`,
      providerId,
      patientId,
      timeFrame,
    ],
    {
      queryFn: fetchGamesTracking(
        http.get as (url: string, params?: unknown) => Promise<unknown>,
      ),
      enabled: !!providerId && !!patientId,
    },
  );

  const stroopTimeSeriesData = React.useMemo(
    () => (stroopQuery.data as StroopTimeSeriesAPIResponse)?.data || [],
    [stroopQuery.data],
  );

  const stroopTimeSeriesAvgData = React.useMemo(
    () => (stroopQuery.data as StroopTimeSeriesAPIResponse)?.absoluteAvgValue || 0,
    [stroopQuery.data],
  );

  const isStroopTimeSeriesDataLoading = stroopQuery.isFetching;

  // Query for 6 months timeframe
  const sixMonthsQuery = useQuery(
    [
      `${gamesUrls.stroopTimeSeries.queryUrl}`,
      providerId,
      patientId,
      TimeFrameOptions.LAST_6_MONTHS,
    ],
    {
      queryFn: fetchGamesTracking(
        http.get as (url: string, params?: unknown) => Promise<unknown>,
      ),
      enabled: !!providerId && !!patientId,
    },
  );

  const hasStroop6MData = React.useMemo(() => {
    const data = (sixMonthsQuery?.data as StroopTimeSeriesAPIResponse)?.data || [];
    return data.length > 0;
  }, [sixMonthsQuery?.data]);

  return {
    stroopTimeSeriesData,
    stroopTimeSeriesAvgData,
    isStroopTimeSeriesDataLoading,
    hasStroop6MData,
  };
}

export default useStroopData;
